import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Input } from "@/components/ui/Input";
import { Popover } from "@/components/ui/Popover";
import { ApiError, apiFetch } from "@/lib/api";
import type { Page, SavedViewResource } from "@/lib/api-types";
import { useIsReadOnly } from "@/lib/current-actor";
import { cx } from "@/lib/cx";
import { validateSavedViewName } from "@/lib/saved-views";

/**
 * The saved-view chip row (R-W4, DESIGN.md `saved-view-chip`) — rendered by
 * a route, above its own `DataTable`, never inside `DataTable` itself
 * (spec Boundaries: "no saved-view-specific rendering path inside the table
 * component"). "All" is always present and always leftmost, even with zero
 * saved views (VC-2.4-11).
 *
 * Selecting a chip is exactly "set the URL to the stored string" — this
 * component never reads sort/filter state itself and never reaches into
 * `DataTable`; the table's own existing URL-reading logic (Story 2.2)
 * reproduces the view once the URL changes, the identical mechanism that
 * reproduces a pasted link (VC-2.4-06).
 *
 * Story 2.6 adds two things: "Save this view" now opens in a `Popover`
 * (this component's live proof of VC-2.6-05, a real popover instance
 * reachable in the app) instead of expanding an inline row, and deleting a
 * view — Story 2.4's own deferred judgement call 2, retired here through
 * `ConfirmDialog` exactly like `UsersList.tsx`'s remove-a-user retrofit.
 */
export interface SavedViewChipsProps {
  /** Scopes this list's saved views from every other list's (spec judgement
   * call 3) — matches whatever key the owning route uses for itself. */
  listKey: string;
}

export function SavedViewChips({ listKey }: SavedViewChipsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isReadOnly = useIsReadOnly();
  const queryClient = useQueryClient();
  const [isNaming, setIsNaming] = useState(false);
  const [name, setName] = useState("");
  const [saveError, setSaveError] = useState<string | undefined>();
  /** `null` means no delete confirmation is open. */
  const [deleteTarget, setDeleteTarget] = useState<SavedViewResource | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  /** A stable fallback focus target for the delete confirm dialog — the
   * same reasoning as `UsersList.tsx`'s `headingRef`: confirming removes
   * the chip (and its "Delete" trigger) as part of the same action, but
   * the row that holds every chip persists. */
  const chipRowRef = useRef<HTMLDivElement>(null);

  const queryKey = ["saved-views", listKey] as const;
  const viewsQuery = useQuery({
    queryKey,
    queryFn: () => apiFetch<Page<SavedViewResource>>(`/saved-views?list_key=${encodeURIComponent(listKey)}`),
  });
  const views = viewsQuery.data?.data ?? [];

  // The literal current query string — never reconstructed from component
  // state, so there is no way for what gets stored to drift from what the
  // address bar already says (VC-2.4-05: "byte-identical... not a
  // re-derived copy"). `page_size` never appears here: `list-query.ts`'s own
  // top-of-file note is explicit that it is not part of this module's URL
  // grammar, and `DataTable`'s own URL-writing handlers never set it either.
  const currentSearch = searchParams.toString();

  function selectView(queryString: string) {
    navigate({ pathname: location.pathname, search: queryString ? `?${queryString}` : "" });
  }

  function startNaming() {
    setIsNaming(true);
    setName("");
    setSaveError(undefined);
  }

  function cancelNaming() {
    setIsNaming(false);
    setName("");
    setSaveError(undefined);
  }

  async function handleSave() {
    const validationError = validateSavedViewName(name);
    if (validationError) {
      // Refused before any request fires (VC-2.4-07) — `apiFetch` is never
      // called on this path.
      setSaveError(validationError);
      return;
    }
    try {
      const created = await apiFetch<SavedViewResource>("/saved-views", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ list_key: listKey, name: name.trim(), query_string: currentSearch }),
      });
      queryClient.setQueryData<Page<SavedViewResource>>(queryKey, (existing) =>
        existing
          ? {
              data: [...existing.data, created],
              meta: { ...existing.meta, total: existing.meta.total + 1 },
            }
          : { data: [created], meta: { total: 1, page: 1, page_size: 1 } },
      );
      cancelNaming();
    } catch (error) {
      setSaveError(error instanceof ApiError ? error.message : "Something went wrong. Please try again.");
    }
  }

  /**
   * Deleting a saved view (Story 2.6, retiring Story 2.4's own judgement
   * call 2). Runs the new, already-audited `DELETE /saved-views/{id}`
   * (`app/api/saved_views.py`) — this dialog is a UI trigger and a
   * confirmation, no new domain logic. If the view being deleted is the
   * one currently selected, the list falls back to "All" rather than
   * leaving the address bar pointed at a filter that no chip represents
   * anymore.
   */
  async function confirmDelete() {
    if (deleteTarget === null) {
      return;
    }
    setIsDeleting(true);
    setDeleteError(undefined);
    try {
      await apiFetch<{ deleted: boolean }>(`/saved-views/${deleteTarget.id}`, { method: "DELETE" });
      queryClient.setQueryData<Page<SavedViewResource>>(queryKey, (existing) =>
        existing === undefined
          ? existing
          : {
              ...existing,
              data: existing.data.filter((view) => view.id !== deleteTarget.id),
              meta: { ...existing.meta, total: Math.max(0, existing.meta.total - 1) },
            },
      );
      if (currentSearch === deleteTarget.query_string) {
        selectView("");
      }
      setDeleteTarget(null);
    } catch (error) {
      setDeleteError(error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="mb-4">
      <div ref={chipRowRef} tabIndex={-1} className="flex items-center gap-2 overflow-x-auto pb-1">
        <SavedViewChip label="All" isActive={currentSearch === ""} onClick={() => selectView("")} />
        {views.map((view) => (
          <SavedViewChip
            key={view.id}
            label={view.name}
            isActive={currentSearch === view.query_string}
            onClick={() => selectView(view.query_string)}
            {...(isReadOnly ? {} : { onDelete: () => setDeleteTarget(view) })}
          />
        ))}
        {/* Read-only rendering is by absence, not `disabled` (DESIGN.md § Do's
         * and Don'ts) — the same withholding mechanism every other screen
         * uses since Story 2.1 (VC-2.4-13). */}
        {!isReadOnly ? (
          <Popover
            open={isNaming}
            onOpenChange={(open) => {
              if (open) {
                startNaming();
              } else {
                cancelNaming();
              }
            }}
            trigger={
              <Button type="button" variant="ghost" className="h-tap-target-min shrink-0">
                Save this view
              </Button>
            }
          >
            <form
              className="flex w-56 flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                void handleSave();
              }}
            >
              <label className="flex flex-col gap-1">
                {/* A persistent visible label, never a placeholder standing
                 * in for one (DESIGN.md § Components "Input / select") — the
                 * same rule the filter-row `<Select>`s in `DataTable.tsx`
                 * already follow. */}
                <span className="text-label font-medium text-ink-secondary">View name</span>
                <Input autoFocus value={name} onChange={(event) => setName(event.target.value)} />
              </label>
              {saveError ? (
                <p role="alert" className="text-meta text-danger">
                  {saveError}
                </p>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={cancelNaming}>
                  Cancel
                </Button>
                <Button type="submit" variant="secondary">
                  Save
                </Button>
              </div>
            </form>
          </Popover>
        ) : null}
      </div>
      {deleteTarget ? (
        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteTarget(null);
              setDeleteError(undefined);
            }
          }}
          title="Delete this view?"
          description={`Deleting "${deleteTarget.name}" removes it from this list of saved views. The properties, tenants, and other data it filtered are not affected.`}
          confirmLabel="Delete this view"
          confirmingLabel="Deleting…"
          isConfirming={isDeleting}
          onConfirm={() => void confirmDelete()}
          fallbackFocusRef={chipRowRef}
          {...(deleteError ? { errorMessage: deleteError } : {})}
        />
      ) : null}
    </div>
  );
}

function SavedViewChip({
  label,
  isActive,
  onClick,
  onDelete,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  onDelete?: () => void;
}) {
  // The same "44px hit area around a 36px visual pill" pattern
  // `FilterChip.tsx` already establishes for this exact DESIGN.md token.
  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={onClick}
        aria-pressed={isActive}
        className="inline-flex h-tap-target-min shrink-0 items-center rounded-full"
      >
        <span
          className={cx(
            "inline-flex h-chip items-center whitespace-nowrap rounded-full px-3 text-meta",
            isActive ? "bg-accent-quiet text-accent" : "bg-neutral-quiet text-ink-secondary",
          )}
        >
          {label}
        </span>
      </button>
      {onDelete ? (
        // A real text label, never an icon alone (EXPERIENCE.md: "icon-only
        // ... destructive actions" is banned everywhere) — always visible,
        // never hover-only, so it works identically with a mouse or a
        // finger (EXPERIENCE.md: "any action available only on hover has a
        // non-hover equivalent" — on this touch-first chip row, that
        // equivalent is simply always being there).
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete the "${label}" view`}
          className="inline-flex h-tap-target-min shrink-0 items-center whitespace-nowrap rounded-full px-2 text-meta text-ink-secondary hover:bg-surface-hover"
        >
          Delete
        </button>
      ) : null}
    </span>
  );
}
